import { createContext, useEffect } from 'react';
import React from 'react';

import { Alert, Box, Button, Spinner } from '@amzn/awsui-components-react-v3';

import { getInitApplicationData } from '../boost/utils';

export enum AppType {
  BOOST = 'BOOST',
  ARGO = 'ARGO',
}

interface AppState {
  appType: AppType;
  context: any;
  loggerClient: any;
  metricsClient: any;
}

export const AppContext = createContext<AppState>({});

interface AppContextProviderProps {
  appType: AppType;
  children: React.ReactNode;
  initialContext?: any;
}

export const AppContextError = ({ onHandleRetryInitData }: { onHandleRetryInitData: () => void }) => {
  return (
    <Box margin="s">
      <Alert
        statusIconAriaLabel="Error"
        type="error"
        header={'Error while trying to get the worker details'}
        action={
          <Button variant="primary" onClick={onHandleRetryInitData}>
            Retry
          </Button>
        }
      >
        try again to get the worker details
      </Alert>
    </Box>
  );
};

export const AppContextProvider = ({ appType, initialContext = {}, children }: AppContextProviderProps) => {
  const metricsClient = {};
  const loggerClient = {};
  const [context, setContext] = React.useState({
    loading: appType === AppType.BOOST,
    error: null,
    data: appType === AppType.ARGO ? initialContext : null,
    ...initialContext,
  });

  const selectedAppContext = appType === AppType.BOOST ? context : initialContext;
  const canLoadApp = !context.error && !context.loading && selectedAppContext;
  const fetchInitBoostApplicationData = () => {
    getInitApplicationData()
      .then((data) => setContext((context) => ({ ...context, loading: false, user: data.user, data, error: null })))
      .catch((e) => {
        console.error('getInitApplicationData:error', e);
        setContext((context) => ({ ...context, loading: false, data: null, error: e.message }));
      });
  };

  useEffect(() => {
    if (appType === AppType.BOOST) {
      fetchInitBoostApplicationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onHandleRetryInitData = () => {
    fetchInitBoostApplicationData();
  };
  return (
    <AppContext.Provider value={{ context: selectedAppContext, loggerClient, metricsClient, appType }}>
      {context.loading && !context.error ? <Spinner /> : null}
      {context.error ? <AppContextError onHandleRetryInitData={onHandleRetryInitData} /> : null}
      {canLoadApp && children}
    </AppContext.Provider>
  );
};
